import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject } from '@angular/core'
import { Subject, ReplaySubject, combineLatest, of } from 'rxjs'
import {
  switchMap,
  shareReplay,
  takeUntil,
  scan,
  startWith,
  take,
  map,
  filter,
  distinctUntilChanged,
  skip,
  tap
} from 'rxjs/operators'
import { LOGGER_SERVICE } from 'src/app/logger/logger.config'
import { LoggerService } from 'src/app/logger/logger.interface'
import { FloApiResponse, Meta } from 'src/app/shared/models/api-response.model'
import { Node } from 'src/io/models/node'

export const paginatedResponseDictionary: { [key: string]: FloApiResponse<ReadonlyArray<Node>> } = {}

/**
 * Handle paginated requests against `baseUrl` using provided `queryParams` (ie
 * facets) using Flosports30 core api default pagination interface. Instead of
 * instantiating this model directly, prefer access via Injectable at
 * PaginatedRequestService.getNewPaginatedRequest()
 *
 * @param httpClient - Angular HttpClient or similar interface
 * @param loggerService - FloSports webapp LoggerService
 * @param baseUrl - api endpoint for requests, excluding query params
 * @param limit - number of items to request per page
 * @param initialIndex - page at which to start making requests
 * @param queryParams - query params to include with each request.
 * `queryParams` are declared at construction and remain fixed for the lifetime
 * of the PaginatedRequest. New PaginatedRequests can be created in cases that
 * require dynamic queryParams. Available as a public readonly property for
 * inspection by consumers
 * @param externalFirstResponse - allow for use of an alternate response object
 * to use as the initial response, for use in cases where the first page of
 * data comes from a different source than `baseUrl`
 */
export class PaginatedRequest<T = Node> {
  private readonly ngOnDestroy$ = new Subject<void>()
  /**
   * index of the current page of data to be fetched
   */
  private readonly currentIndex$ = new ReplaySubject<number>(1)

  constructor(
    private readonly httpClient: HttpClient,
    @Inject(LOGGER_SERVICE) private readonly loggerService: LoggerService,
    private readonly baseUrl: string,
    private readonly limit = 15,
    private readonly initialIndex = 1,
    public readonly queryParams: Record<string, string | undefined> = {},
    private readonly externalFirstResponse?: FloApiResponse<ReadonlyArray<Node>>
  ) {
    this.queryParams = compactObject(queryParams)
    if (!this.baseUrl) {
      this.loggerService.error('missing baseUrl for PaginatedRequest')
    }
    const currentIndex = externalFirstResponse?.meta?.page || this.initialIndex
    this.currentIndex$.next(currentIndex)
  }

  /**
   * Contains the full body of the most recent response received, including
   * `meta` and `data`. If `externalFirstResponse` is provided to the
   * constructor, it is used here in place of making the first network request
   */
  public readonly currentResponse$ = this.currentIndex$.pipe(
    this.externalFirstResponse ? skip(1) : map(x => x),
    switchMap(index => {
      const params = new HttpParams({
        fromObject: {
          ...this.queryParams,
          limit: `${this.limit}`,
          page: `${index}`
        }
      })
      const key = this.baseUrl + params.toString()

      // if the dictionary has a response cached
      if (!!paginatedResponseDictionary[key]) {
        // return the value as an observable
        return of(paginatedResponseDictionary[key])
      }

      const request = this.httpClient
        .get<FloApiResponse<ReadonlyArray<Node>>>(this.baseUrl, {
          params
        })
        .pipe(
          tap(response => {
            paginatedResponseDictionary[key] = response
            if (Object.keys(paginatedResponseDictionary).length > 10) {
              // keep in-memory usage low by trimming this local cache as we add
              delete paginatedResponseDictionary[Object.keys(paginatedResponseDictionary)[0]]
            }
          })
        )
      return request
    }),
    this.externalFirstResponse ? startWith(this.externalFirstResponse) : map(x => x),
    map(response => {
      if (
        this.queryParams &&
        !!this.queryParams.include_node &&
        response?.meta?.extra &&
        response?.meta?.extra[2]?.include_node &&
        !!response.data
      ) {
        const includeNode = response.meta.extra[2].include_node
        delete this.queryParams.include_node
        return {
          ...response,
          data: [
            // TODO: remove includeNode and expose separately
            includeNode,
            ...response.data.filter(node => node.id !== includeNode.id)
          ]
        }
      } else {
        return response
      }
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true
    }),
    takeUntil(this.ngOnDestroy$)
  )

  /**
   * if the `include_node` queryParam is provided to the constructor, the
   * response data is exposed here. The node is only requested on the first
   * call to the api and preserved here for the lifetime of the
   * PaginatedRequest
   */
  public readonly includeNode$ = this.currentResponse$.pipe(
    map(
      response =>
        response &&
        response.meta &&
        response.meta.extra &&
        response.meta.extra[2] &&
        response.meta.extra[2].include_node
    ),
    filter(node => !!node),
    take(1),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  )

  /**
   * the maximum number of pages available to request, as informed by the api
   */
  public readonly maxPages$ = this.currentResponse$.pipe(
    map(response => response.meta.pages as number),
    shareReplay({ bufferSize: 1, refCount: false })
  )

  /**
   * the total number of results available, as informed by the api
   */
  public readonly totalResults$ = this.currentResponse$.pipe(map(response => response.meta.total))

  /**
   * page number of the current response
   */
  public readonly currentResponsePageNumber$ = this.currentResponse$.pipe(
    map(response => response.meta.page as number),
    distinctUntilChanged()
  )

  /**
   * true if there are no more pages available
   */
  public readonly maxPageReached$ = this.currentResponse$.pipe(
    map(response => response.meta as Meta & { page: number; pages: number }),
    map(meta => meta.page >= meta.pages)
  )

  /**
   * integer indexed dictionary of all pages previously recieved
   *
   * ```
   *              {
   *                1: [Node, Node, Node, Node, Node, Node]
   *                2: [Node, Node, Node, Node, Node, Node]
   *                3: [Node, Node, Node, Node, Node, Node]
   *                6: [Node, Node, Node, Node, Node, Node]
   *              }
   * ```
   */
  public readonly allPageDataDictionary$ = this.currentResponse$.pipe(
    scan(
      (fetchedPageData, response) =>
        response && response.meta && response.meta.page
          ? {
              ...fetchedPageData,
              [response.meta.page]: response.data
            }
          : fetchedPageData,
      {} as Record<string, ReadonlyArray<Node>>
    )
  )

  /**
   * pagination parameters of the currentResponse
   */
  public readonly pagination$ = this.currentResponse$.pipe(
    map(response => response.meta),
    map(meta => ({
      page: meta.page,
      pages: meta.pages,
      limit: meta.limit,
      total: meta.total
    }))
  )

  /**
   * accumulation of all data exposed as a single flattened array
   */
  public readonly allPageDataArray$ = this.allPageDataDictionary$.pipe(
    map(dictionary =>
      Object.keys(dictionary).reduce((allPageData, key) => [...allPageData, ...(dictionary[key] || [])], [])
    ),
    shareReplay({
      refCount: true,
      bufferSize: 1
    })
  )

  /**
   * data from the current response
   */
  public readonly currentPageData$ = this.currentResponse$.pipe(map(response => response?.data || []))

  /**
   * true if `currentIndex$` does not equal `currentResponsePageNumber`, ie the
   * request index has updated but the response index has not
   */
  public readonly isLoading$ = combineLatest([this.currentIndex$, this.currentResponsePageNumber$]).pipe(
    map(([currentIndex, currentPageNumber]) => currentIndex === currentPageNumber)
  )

  public destroy() {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
    this.currentIndex$.complete()
  }

  /**
   * request a specific page
   */
  public goToPage = (page: number) =>
    this.maxPages$.pipe(take(1)).subscribe(maxPages => page <= maxPages && this.currentIndex$.next(page))

  /**
   * request the next page
   */
  public goToNextPage = () => this.currentIndex$.pipe(take(1)).subscribe(index => this.goToPage(index + 1))
}

/*
 * remove keys with falsy values
 */
const compactObject = (inputObject?: Record<string, string | undefined>): Record<string, string> =>
  !!inputObject
    ? Object.keys(inputObject).reduce(
        (result, key) => (inputObject[key] ? { ...result, [key]: inputObject[key] } : result),
        {}
      )
    : {}
